import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentTextIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  ArrowDownTrayIcon,
  BellIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';

interface Contract {
  id: string;
  partnerName: string;
  partnerNameAr: string;
  title: string;
  titleAr: string;
  type: 'discount' | 'service' | 'supply' | 'licensing' | 'partnership';
  status: 'active' | 'pending' | 'expired' | 'suspended' | 'draft';
  startDate: string;
  endDate: string;
  value: number;
  terms: {
    discountRate?: number;
    minimumSpend?: number;
    paymentTerms: string;
  };
  milestones: {
    id: string;
    title: string;
    status: 'completed' | 'pending' | 'overdue';
  }[];
  notifications: number;
}

interface ContractsManagerProps {
  className?: string;
}

const ContractsManager: React.FC<ContractsManagerProps> = ({ className = '' }) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const generateContracts = (): Contract[] => {
      return [
        {
          id: '1',
          partnerName: 'Google Ads',
          partnerNameAr: 'إعلانات جوجل',
          title: 'Marketing Partnership Agreement',
          titleAr: 'اتفاقية شراكة تسويقية',
          type: 'discount',
          status: 'active',
          startDate: '2024-01-01',
          endDate: '2024-12-31',
          value: 50000,
          terms: {
            discountRate: 20,
            minimumSpend: 5000,
            paymentTerms: 'Net 30'
          },
          milestones: [
            { id: '1', title: 'Initial Setup', status: 'completed' },
            { id: '2', title: 'Q1 Review', status: 'completed' },
            { id: '3', title: 'Mid-Year Assessment', status: 'pending' }
          ],
          notifications: 1
        },
        {
          id: '2',
          partnerName: 'Microsoft 365',
          partnerNameAr: 'مايكروسوفت 365',
          title: 'Software Licensing Agreement',
          titleAr: 'اتفاقية ترخيص البرمجيات',
          type: 'licensing',
          status: 'active',
          startDate: '2024-02-01',
          endDate: '2025-01-31',
          value: 75000,
          terms: {
            discountRate: 15,
            minimumSpend: 10000,
            paymentTerms: 'Annual prepayment'
          },
          milestones: [
            { id: '4', title: 'License Activation', status: 'completed' },
            { id: '5', title: 'Training Completion', status: 'completed' },
            { id: '6', title: 'Annual Review', status: 'pending' }
          ],
          notifications: 1
        },
        {
          id: '3',
          partnerName: 'AWS Cloud',
          partnerNameAr: 'أمازون ويب سيرفيسز',
          title: 'Cloud Services Agreement',
          titleAr: 'اتفاقية الخدمات السحابية',
          type: 'service',
          status: 'pending',
          startDate: '2024-07-01',
          endDate: '2025-06-30',
          value: 120000,
          terms: {
            discountRate: 25,
            minimumSpend: 15000,
            paymentTerms: 'Monthly billing'
          },
          milestones: [
            { id: '7', title: 'Infrastructure Setup', status: 'pending' },
            { id: '8', title: 'Migration Phase 1', status: 'pending' }
          ],
          notifications: 2
        }
      ];
    };

    setTimeout(() => {
      setContracts(generateContracts());
      setLoading(false);
    }, 1000);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'expired': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircleIcon className="w-5 h-5" />;
      case 'pending': return <ClockIcon className="w-5 h-5" />;
      case 'expired': return <XCircleIcon className="w-5 h-5" />;
      default: return <DocumentTextIcon className="w-5 h-5" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'discount': return <CurrencyDollarIcon className="w-5 h-5 text-white" />;
      case 'service': return <GlobeAltIcon className="w-5 h-5 text-white" />;
      case 'licensing': return <ShieldCheckIcon className="w-5 h-5 text-white" />;
      case 'partnership': return <UserGroupIcon className="w-5 h-5 text-white" />;
      default: return <DocumentTextIcon className="w-5 h-5 text-white" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-SA', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA');
  };

  const filteredContracts = contracts.filter(contract => {
    if (filterStatus !== 'all' && contract.status !== filterStatus) return false;
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return contract.partnerNameAr.toLowerCase().includes(searchLower) ||
             contract.titleAr.toLowerCase().includes(searchLower);
    }
    return true;
  });

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"
          />
          <span className="mr-3 text-gray-600 dark:text-gray-400">
            جاري تحميل العقود والاتفاقيات...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg ${className}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-6 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 rtl:space-x-reverse">
            <ClipboardDocumentCheckIcon className="w-8 h-8 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">إدارة العقود والاتفاقيات</h2>
              <p className="text-purple-100">متابعة وإدارة جميع عقود الشراكات</p>
            </div>
          </div>
          <button className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 rtl:space-x-reverse">
            <PlusIcon className="w-5 h-5" />
            <span>عقد جديد</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ابحث عن شريك أو عقد..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="pending">قيد الانتظار</option>
              <option value="expired">منتهي الصلاحية</option>
            </select>
          </div>

          <div>
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 rtl:space-x-reverse">
              <ArrowDownTrayIcon className="w-5 h-5" />
              <span>تصدير التقرير</span>
            </button>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {filteredContracts.map((contract, index) => (
            <motion.div
              key={contract.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4 rtl:space-x-reverse">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center">
                      {getTypeIcon(contract.type)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {contract.partnerNameAr}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-2">
                      {contract.titleAr}
                    </p>
                    <div className="flex items-center space-x-4 rtl:space-x-reverse text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center space-x-1 rtl:space-x-reverse">
                        <CalendarDaysIcon className="w-4 h-4" />
                        <span>{formatDate(contract.startDate)} - {formatDate(contract.endDate)}</span>
                      </span>
                      <span className="flex items-center space-x-1 rtl:space-x-reverse">
                        <CurrencyDollarIcon className="w-4 h-4" />
                        <span>{formatCurrency(contract.value)}</span>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <span className={`inline-flex items-center space-x-1 rtl:space-x-reverse px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                    {getStatusIcon(contract.status)}
                    <span>
                      {contract.status === 'active' ? 'نشط' :
                       contract.status === 'pending' ? 'قيد الانتظار' :
                       contract.status === 'expired' ? 'منتهي' : 'مسودة'}
                    </span>
                  </span>
                  
                  {contract.notifications > 0 && (
                    <div className="relative">
                      <BellIcon className="w-5 h-5 text-orange-500" />
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                        {contract.notifications}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contract Terms Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {contract.terms.discountRate && (
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {contract.terms.discountRate}%
                    </div>
                    <div className="text-xs text-green-600 dark:text-green-400">نسبة الخصم</div>
                  </div>
                )}
                
                {contract.terms.minimumSpend && (
                  <div className="text-center p-3 bg-blue-50 dark:bg-blue-900 rounded-lg">
                    <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(contract.terms.minimumSpend)}
                    </div>
                    <div className="text-xs text-blue-600 dark:text-blue-400">الحد الأدنى</div>
                  </div>
                )}
                
                <div className="text-center p-3 bg-purple-50 dark:bg-purple-900 rounded-lg">
                  <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                    {contract.milestones.filter(m => m.status === 'completed').length}/{contract.milestones.length}
                  </div>
                  <div className="text-xs text-purple-600 dark:text-purple-400">المعالم المكتملة</div>
                </div>
                
                <div className="text-center p-3 bg-orange-50 dark:bg-orange-900 rounded-lg">
                  <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
                    {contract.terms.paymentTerms}
                  </div>
                  <div className="text-xs text-orange-600 dark:text-orange-400">شروط الدفع</div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <button className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition-colors">
                    <EyeIcon className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900 rounded-lg transition-colors">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900 rounded-lg transition-colors">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  آخر تحديث: {formatDate(contract.startDate)}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContractsManager;
